import { Ingredient } from "src/api/client-api/products/useGetOneProduct";
import { IngredientPost } from "src/api/client-api/post/useGetSomeIngredientPosts";
import { IngredientReference } from "src/api/sure-api/ingredientReference/useGetAllIngredientReference";

interface ProductIngredient {
    ingredient: Ingredient;
}

type SummaryScoreRef = {
    label: string;
    score: number;
};

export type Summary = {
    isValid: boolean;
    refIngredient: SummaryScoreRef | undefined;

    completion: number;
    ssqFilled: number;
    ssqTotalCount: number;
};
export type IngredientSummaryMapping = Partial<Record<number, Summary>>;

export type IngredientSummaryData = {
    averageCompletion: number;
    ingredientsCount: number;
    byIngredientId: IngredientSummaryMapping;
};

export class IngredientCompletionService {
    private static supplierCompletionRates(
        ingredient: Ingredient | undefined | null,
        ingredientPosts: IngredientPost[],
    ): number[] {
        if (!ingredient) {
            return [];
        }

        if (!ingredient.ingredientSuppliers?.length) {
            return [];
        }

        return ingredient.ingredientSuppliers.map((ingredientSupplier) => {
            const supplierId = ingredientSupplier?.supplier?.id;
            const companyMatch = ingredientPosts.find((post) => post.company?.id === supplierId);
            if (companyMatch === undefined) {
                // Not found somehow
                return 0;
            }
            const result = companyMatch.completions.ssq.result;
            return result;
        });
    }

    private static computeCompletion = (
        ingredient: Ingredient | undefined | null,
        ingredientPosts: IngredientPost[],
    ) => {
        const suppliersCompletion = this.supplierCompletionRates(ingredient, ingredientPosts);

        const acc = { summedCompletions: 0, fullyCompleted: 0 };
        const ingredientCompletion = suppliersCompletion.reduce((prevAcc, supplierCompletion) => {
            prevAcc.summedCompletions += supplierCompletion;

            const isFullyAnswered = supplierCompletion === 100 ? 1 : 0;
            prevAcc.fullyCompleted += isFullyAnswered;
            return prevAcc;
        }, acc);

        const total = suppliersCompletion.reduce((a, c) => a + c, 0);
        const averageIngredientCompletion = total / suppliersCompletion.length;

        return {
            completion: averageIngredientCompletion,
            fullyCompletedQuestionnaire: ingredientCompletion.fullyCompleted,
            suppliersCount: suppliersCompletion.length,
        };
    };

    public static calculateTotalCompletionRates = (
        productIngredients: ProductIngredient[] | undefined | null,
        ingredientPosts: IngredientPost[] | undefined | null,
        ingredientReferences: IngredientReference[] | undefined | null,
        scoreId: number,
    ): IngredientSummaryData => {
        type ExtraSummaryAgg = {
            suppliersWithQuestionnaireCount: number;
            totalIngredientCompletion: number;
        };
        const summaryExtraAcc: ExtraSummaryAgg = {
            totalIngredientCompletion: 0,
            suppliersWithQuestionnaireCount: 0,
        };
        const emptyAcc: IngredientSummaryData & ExtraSummaryAgg = {
            averageCompletion: 0,
            ingredientsCount: 0,
            byIngredientId: {},

            ...summaryExtraAcc,
        };

        const isInputInvalid = !productIngredients?.length || !ingredientPosts?.length;
        if (isInputInvalid) {
            return emptyAcc;
        }

        const ingredientCompletions = productIngredients.reduce((prevAcc, productIngredient) => {
            const ingId = productIngredient.ingredient.id;
            const posts = ingredientPosts.filter((post) => post.ingredient.id === ingId);

            const ingRefId = productIngredient.ingredient.ingredientReferenceId;
            const ingredientReference = ingredientReferences?.find((ir) => ir.id === ingRefId);
            const ingredientReferenceHasSsq = !!ingredientReference?.ssq;
            const isValid = ingredientReferenceHasSsq || ingredientPosts.length > 0;

            const completion = this.computeCompletion(productIngredient.ingredient, posts);

            const scoreRef = ingredientReference?.scores?.find((score) => score.score?.id === scoreId);
            const byIdSummary: Summary = {
                isValid,
                refIngredient: {
                    score: scoreRef?.value ?? -1,
                    label: ingredientReference?.name ?? "",
                },
                completion: completion.completion,
                ssqFilled: completion.fullyCompletedQuestionnaire,
                ssqTotalCount: completion.suppliersCount,
            };

            // Don't count the ingredientsWithoutSSQ ?
            prevAcc.ingredientsCount += isValid ? 1 : 0;
            prevAcc.byIngredientId[ingId] = byIdSummary;

            prevAcc.totalIngredientCompletion += completion.completion;
            prevAcc.suppliersWithQuestionnaireCount += isValid ? 1 : 0;
            return prevAcc;
        }, emptyAcc);

        const averageCompletion =
            ingredientCompletions.suppliersWithQuestionnaireCount === 0 ?
                0
            :   ingredientCompletions.totalIngredientCompletion / ingredientCompletions.suppliersWithQuestionnaireCount;
        ingredientCompletions.averageCompletion = averageCompletion;

        return ingredientCompletions;
    };
}
